import { pickTeamDefaultOrganization } from './pick-team-default-organization';

interface UrlSelectionInput<T extends { id: number }> {
  /** The organisations the selector lists: the viewer's home-view organisations. */
  homeViewOrganizations: T[] | undefined;
  /** The `organizationId` query parameter, as the URL carries it. */
  organizationIdParam: string | null;
  /** The organisation a route names in its path (the create-company wizard), if any. */
  pathOrganizationId: string | undefined;
}

/**
 * The organisation the URL names, when it names one the viewer can reach: the query parameter
 * first, then the path. `null` when it names none, or only one that is stale or inaccessible.
 *
 * An explicit selection, as against the default the selector falls back to. The Contact dialog
 * prefills from this alone for a customer, because on a page without the selector a default
 * picked from the home-view organisations can differ from the one the rest of the product uses.
 */
export function organizationNamedByUrl<T extends { id: number }>({
  homeViewOrganizations,
  organizationIdParam,
  pathOrganizationId,
}: UrlSelectionInput<T>): T | null {
  if (!homeViewOrganizations?.length) {
    return null;
  }
  // A valid, accessible org id in the URL wins. `organizationId=none` is not one: the selector
  // used to offer a "None" row, and picking it left every org-scoped view on this page with
  // nothing to render against, which for a customer with one organisation meant emptying their
  // own home with no obvious way back (PLT-6696). The row is gone and the value is no longer
  // written, so what is left here is old links and bookmarks: they fall through to the default
  // rather than reproducing the blank page they were saved from.
  if (organizationIdParam != null) {
    const match = homeViewOrganizations.find(org => String(org.id) === organizationIdParam);
    if (match) {
      return match;
    }
    // Otherwise the id is stale/inaccessible — fall through to the path.
  }
  // No param, but the route itself names an organization (the create-company
  // wizard). That is the org the page is about — and the one the company is
  // being created for — so prefer it over the default.
  if (pathOrganizationId != null) {
    const pathMatch = homeViewOrganizations.find(org => String(org.id) === pathOrganizationId);
    if (pathMatch) {
      return pathMatch;
    }
  }
  return null;
}

/**
 * Which organisation the selector has picked, as a pure function of what the URL and the
 * organisations query say: the one the URL names, otherwise the default.
 *
 * `useBuildSelectedOrganization` is the provider's builder around this, and also persists a
 * path-adopted id into the URL. The default is deterministic, so a reload is stable and the URL
 * stays clean until the user actively switches.
 */
export function resolveSelectedOrganization<T extends { id: number }>({
  isTeam,
  teamDefaultOrganizationId,
  ...urlSelection
}: UrlSelectionInput<T> & {
  isTeam: boolean;
  teamDefaultOrganizationId: number | null | undefined;
}): T | null {
  const { homeViewOrganizations } = urlSelection;
  if (!homeViewOrganizations?.length) {
    // Organizations not loaded yet (or none accessible): nothing to select until there is.
    return null;
  }
  const named = organizationNamedByUrl(urlSelection);
  if (named) {
    return named;
  }
  if (isTeam) {
    return pickTeamDefaultOrganization(homeViewOrganizations, teamDefaultOrganizationId);
  }
  return homeViewOrganizations[0];
}
